import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { AiOutlineFolderView } from 'react-icons/ai';
import { useHandleDeletion } from '@/components/AppBar/SelectPopover/hooks/useHandleDeletion';
import useViews from '@/components/AppBar/SelectPopover/hooks/useViews';
import { buildViewSwitchUrl } from '@/components/AppShell/networkRoutes';
import ConfirmationDialog from '@/components/util-component/Dialog/ConfirmationDialog';
import { useGlobalState } from '@/global-state/useGlobalState';
import { useCreateView } from '@/hooks/network/useCreateView';
import { useNetworkEditable } from '@/hooks/network/useNetworkEditable';
import { useRefetchView } from '@/hooks/useView';
import { useAsyncRoutePush } from '@/hooks/utils/useAsyncRouterPush';
import { mutationWithFeedback } from '@/utils/toast';
import { trpc } from '@/utils/trpc';
import CreateInput from './CreateInput';
import { generateDeleteMenuItem, generateDuplicateViewMenuItem, generateRenameMenuItem } from './menuItems';
import MergeViewButton from './MergeViewButton';
import { ActionMode, MergeViewDialogData, ViewMergeConfirmData } from './MergeViewDialog';
import VirtualizedSelectPopover, { MenuItems, Title } from './VirtualizedSelectPopover';

// Lazy load MergeViewDialog to avoid bundling react-select on initial page load
const MergeViewDialog = dynamic(() => import('./MergeViewDialog').then(mod => mod.default), {
  ssr: false,
  loading: () => <div data-testid="merge-dialog-loading" style={{
    padding: '20px'
  }}>
        Loading merge dialog...
      </div>
});
type ViewPopoverContainerProps = {
  currentViewId: string | undefined;
  currentNetworkId: string;
  updateTimestamps: Array<{
    viewId: string;
    updateTime: string;
  }> | undefined;
  // Records the chosen viewId into sticky state (typically
  // localStorage-backed) when the current route has no place for a view
  // in its URL (e.g. /assistant, network-diff). On those routes picking a
  // view does not navigate; everywhere the view lives in the URL the
  // popover navigates instead so the page refreshes. See
  // `buildViewSwitchUrl`.
  onSelectViewWithoutNavigate?: (viewId: string) => void;
  // Suppresses the "auto-open when no view is selected" behavior. The
  // selector now renders on every page; auto-opening only makes sense on
  // the digraph (where picking a view is the primary action). On other
  // pages an auto-opened popover would cover the page on load.
  disableAutoOpen?: boolean;
};
export function ViewPopoverContainer(t0) {
  const $ = _c(91);
  const {
    currentViewId,
    currentNetworkId,
    updateTimestamps,
    onSelectViewWithoutNavigate,
    disableAutoOpen: t1
  } = t0;
  const disableAutoOpen = t1 === undefined ? false : t1;
  const asyncPush = useAsyncRoutePush();
  const currentUrl = usePathname() ?? "";
  const confirmationDialogRef = useRef(null);
  const handleViewChange = useGlobalState(_temp);
  const {
    mutateAsync: createView,
    isPending: isPendingCreate
  } = useCreateView();
  const {
    views,
    isLoading: isLoadingViews,
    refetch: refetchViews
  } = useViews(currentNetworkId, updateTimestamps);
  const {
    isReleased
  } = useNetworkEditable(currentNetworkId);
  const refetchView = useRefetchView();
  const {
    mutateAsync: renameView,
    isPending: isPendingRename
  } = trpc.view.renameView.useMutation();
  const {
    mutateAsync: copyViewPositions,
    isPending: isCopyPending
  } = trpc.view.copyViewPositions.useMutation();
  const {
    mutateAsync: mergeViewPositions,
    isPending: isMergePending
  } = trpc.view.mergeViewPositions.useMutation();
  const {
    mutateAsync: duplicateView,
    isPending: isDuplicatePending
  } = trpc.view.duplicateView.useMutation();
  const trpcUtils = trpc.useUtils();
  const [deleteConfirmationProps, setDeleteConfirmationProps] = useState(undefined);
  const [mergeDialogData, setMergeDialogData] = useState(undefined);
  let t2;
  if ($[0] !== refetchViews) {
    t2 = async () => {
      await refetchViews();
    };
    $[0] = refetchViews;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    handleDeletion,
    isPendingDelete
  } = useHandleDeletion(currentViewId, currentNetworkId, t2);
  const isPending = isPendingCreate || isLoadingViews || views === undefined || isPendingRename || isPendingDelete || isCopyPending || isMergePending || isDuplicatePending;
  let t3;
  if ($[2] !== isReleased || $[3] !== views) {
    t3 = new Set(isReleased ? (views ?? []).filter(_temp2).map(_temp3) : []);
    $[2] = isReleased;
    $[3] = views;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const lockedViewIds = t3;
  let t4;
  if ($[5] !== handleDeletion) {
    t4 = t5 => {
      const {
        id,
        name
      } = t5;
      setDeleteConfirmationProps({
        viewName: name,
        onConfirm: async () => {
          await handleDeletion(id);
        }
      });
    };
    $[5] = handleDeletion;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleDeleteAttempt = t4;
  let t5;
  if ($[7] !== asyncPush || $[8] !== currentNetworkId || $[9] !== duplicateView || $[10] !== handleDeleteAttempt || $[11] !== handleViewChange || $[12] !== lockedViewIds || $[13] !== renameView || $[14] !== trpcUtils.view.getViews || $[15] !== views) {
    t5 = id_0 => {
      const isLocked = lockedViewIds.has(id_0 ?? "");
      const rename = generateRenameMenuItem(async t6 => {
        const {
          id: id_1,
          name: name_0
        } = t6;
        return await mutationWithFeedback({
          mutation: renameView({
            viewId: id_1,
            newName: name_0
          }),
          successText: "Renamed view!",
          errorText: "Failed to rename view!"
        });
      });
      const duplicate = generateDuplicateViewMenuItem(async t7 => {
        const {
          id: id_2,
          name: name_1
        } = t7;
        const {
          viewId
        } = await mutationWithFeedback({
          mutation: duplicateView({
            viewId: id_2,
            viewName: name_1
          }),
          successText: "Duplicated view!",
          errorText: "Failed to duplicate view!"
        });
        await trpcUtils.view.getViews.invalidate({
          networkId: currentNetworkId
        });
        handleViewChange();
        await asyncPush(`/${currentNetworkId}/${viewId}`);
      }, (views ?? []).map(_temp4));
      const remove = generateDeleteMenuItem(handleDeleteAttempt);
      return [{
        ...rename,
        props: {
          ...rename.props,
          disabled: isLocked
        }
      }, duplicate, {
        ...remove,
        props: {
          ...remove.props,
          disabled: isLocked
        }
      }];
    };
    $[7] = asyncPush;
    $[8] = currentNetworkId;
    $[9] = duplicateView;
    $[10] = handleDeleteAttempt;
    $[11] = handleViewChange;
    $[12] = lockedViewIds;
    $[13] = renameView;
    $[14] = trpcUtils.view.getViews;
    $[15] = views;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const menuItems = t5;
  const [filterValue, setFilterValue] = useState("");
  let t6;
  if ($[17] !== currentNetworkId || $[18] !== currentUrl || $[19] !== filterValue || $[20] !== lockedViewIds || $[21] !== views) {
    t6 = views?.filter(view_2 => view_2.name.toLowerCase().includes(filterValue.toLowerCase().trim())).map(view_3 => ({
      ...view_3,
      isViewLocked: lockedViewIds.has(view_3.id),
      url: buildViewSwitchUrl(currentUrl, currentNetworkId, view_3.id) ?? currentUrl
    })) ?? [];
    $[17] = currentNetworkId;
    $[18] = currentUrl;
    $[19] = filterValue;
    $[20] = lockedViewIds;
    $[21] = views;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const viewOptions = t6;
  let t7;
  if ($[23] !== filterValue || $[24] !== views) {
    t7 = views?.findIndex(n => n.name === filterValue.trim());
    $[23] = filterValue;
    $[24] = views;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const isViewCreatable = t7 === -1;
  let t8;
  if ($[26] !== asyncPush || $[27] !== createView || $[28] !== currentNetworkId) {
    t8 = async name_2 => {
      const id_3 = await createView({
        name: name_2,
        networkId: currentNetworkId
      });
      await asyncPush(`/${currentNetworkId}/${id_3}`);
    };
    $[26] = asyncPush;
    $[27] = createView;
    $[28] = currentNetworkId;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const handleCreate = t8;
  let t9;
  if ($[30] !== currentNetworkId || $[31] !== trpcUtils.network.getNetworksOrderedByLastModification) {
    t9 = async () => {
      const networks = await trpcUtils.network.getNetworksOrderedByLastModification.fetch();
      setMergeDialogData({
        networks,
        currentNetworkId
      });
    };
    $[30] = currentNetworkId;
    $[31] = trpcUtils.network.getNetworksOrderedByLastModification;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const handleImportAttempt = t9;
  let t10;
  if ($[33] !== asyncPush || $[34] !== copyViewPositions || $[35] !== currentNetworkId || $[36] !== mergeViewPositions) {
    t10 = async (fromViewId, confirmData) => {
      let result;
      if (confirmData.mode.actionMode === ActionMode.Copy) {
        result = await mutationWithFeedback({
          mutation: copyViewPositions({
            networkId: currentNetworkId,
            fromViewId,
            viewName: confirmData.mode.viewName
          }),
          successText: "Copied view!",
          errorText: "Failed to copy view!"
        });
      } else {
        result = await mutationWithFeedback({
          mutation: mergeViewPositions({
            networkId: currentNetworkId,
            fromViewId,
            toViewId: confirmData.mode.toViewId
          }),
          successText: "Merged view!",
          errorText: "Failed to merge view!"
        });
      }
      await asyncPush(`/${currentNetworkId}/${result.viewId}`);
    };
    $[33] = asyncPush;
    $[34] = copyViewPositions;
    $[35] = currentNetworkId;
    $[36] = mergeViewPositions;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const handleMerge = t10;
  let t11;
  if ($[38] !== currentViewId || $[39] !== views) {
    t11 = views?.find(view_4 => view_4.id === currentViewId)?.name;
    $[38] = currentViewId;
    $[39] = views;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const t12 = !isViewCreatable;
  let t13;
  if ($[41] !== handleCreate || $[42] !== t12) {
    t13 = <CreateInput isCreateDisabled={t12} placeholder="Filter or create..." onChange={setFilterValue} onCreate={handleCreate} />;
    $[41] = handleCreate;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== handleImportAttempt) {
    t14 = <MergeViewButton onClick={handleImportAttempt} />;
    $[44] = handleImportAttempt;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== t13 || $[47] !== t14) {
    t15 = <Flex gap={4} justifyContent="space-between" paddingRight={10} width="100%">{t13}{t14}</Flex>;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <AiOutlineFolderView />;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  const t17 = !currentViewId && !disableAutoOpen;
  let t18;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [confirmationDialogRef];
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== deleteConfirmationProps || $[52] !== mergeDialogData) {
    t19 = () => !!deleteConfirmationProps || !!mergeDialogData;
    $[51] = deleteConfirmationProps;
    $[52] = mergeDialogData;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== currentNetworkId || $[55] !== currentUrl || $[56] !== currentViewId || $[57] !== handleViewChange || $[58] !== onSelectViewWithoutNavigate || $[59] !== refetchView) {
    t20 = async id_4 => {
      if (buildViewSwitchUrl(currentUrl, currentNetworkId, id_4) === null) {
        onSelectViewWithoutNavigate?.(id_4);
        return;
      }
      handleViewChange();
      if (currentViewId === id_4) {
        await refetchView(id_4, currentNetworkId);
      }
    };
    $[54] = currentNetworkId;
    $[55] = currentUrl;
    $[56] = currentViewId;
    $[57] = handleViewChange;
    $[58] = onSelectViewWithoutNavigate;
    $[59] = refetchView;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== asyncPush || $[62] !== createView || $[63] !== currentNetworkId) {
    t21 = async name_3 => {
      const id_5 = await createView({
        name: name_3,
        networkId: currentNetworkId
      });
      await asyncPush(`/${currentNetworkId}/${id_5}`);
    };
    $[61] = asyncPush;
    $[62] = createView;
    $[63] = currentNetworkId;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== currentViewId || $[66] !== isPending || $[67] !== menuItems || $[68] !== t11 || $[69] !== t15 || $[70] !== t17 || $[71] !== t19 || $[72] !== t20 || $[73] !== t21 || $[74] !== viewOptions) {
    t22 = <VirtualizedSelectPopover currentOption={currentViewId} currentOptionName={t11} headerItem={t15} icon={t16} isDefaultOpen={t17} isLoading={isPending} menuItems={menuItems} options={viewOptions} popoverPlacement="bottom-end" refOfElementsToIgnoreOutsideClick={t18} shouldPreventClose={t19} title={Title.VIEW} onChangeCurrentOption={t20} onCreate={t21} />;
    $[65] = currentViewId;
    $[66] = isPending;
    $[67] = menuItems;
    $[68] = t11;
    $[69] = t15;
    $[70] = t17;
    $[71] = t19;
    $[72] = t20;
    $[73] = t21;
    $[74] = viewOptions;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  const t23 = !!deleteConfirmationProps;
  const t24 = `Are you sure you want to delete ${deleteConfirmationProps?.viewName}?`;
  let t25;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => {
      setDeleteConfirmationProps(undefined);
    };
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== deleteConfirmationProps) {
    t26 = async () => {
      await deleteConfirmationProps?.onConfirm();
      setDeleteConfirmationProps(undefined);
    };
    $[77] = deleteConfirmationProps;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== t23 || $[80] !== t24 || $[81] !== t26) {
    t27 = <ConfirmationDialog ref={confirmationDialogRef} confirmingActionText="Delete" description="Deleting a view is irreversible." isOpen={t23} title={t24} onClose={t25} onConfirm={t26} />;
    $[79] = t23;
    $[80] = t24;
    $[81] = t26;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== handleMerge || $[84] !== mergeDialogData || $[85] !== trpcUtils.view.getViews) {
    t28 = mergeDialogData && <MergeViewDialog getViewsByNetworkId={async networkId => {
      const views_0 = await trpcUtils.view.getViews.fetch({
        networkId
      });
      return views_0;
    }} mergeDialogData={mergeDialogData} onClose={() => setMergeDialogData(undefined)} onConfirm={handleMerge} />;
    $[83] = handleMerge;
    $[84] = mergeDialogData;
    $[85] = trpcUtils.view.getViews;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  let t29;
  if ($[87] !== t22 || $[88] !== t27 || $[89] !== t28) {
    t29 = <>{t22}{t27}{t28}</>;
    $[87] = t22;
    $[88] = t27;
    $[89] = t28;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  return t29;
}
function _temp4(view_1) {
  return view_1.name;
}
function _temp3(view_0) {
  return view_0.id;
}
function _temp2(view) {
  return !view.createdAfterRelease;
}
function _temp(state) {
  return state.handleViewChange;
}